<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && vehicle"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="handleClose"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <!-- Header -->
            <div class="sticky top-0 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div class="flex items-center">
                <div class="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center mr-3">
                  <svg class="h-5 w-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                  Edit Vehicle
                </h3>
              </div>
              <button
                @click="handleClose"
                class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="handleSubmit" class="px-6 py-4">
              <!-- Vehicle Information -->
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Vehicle Information</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Vehicle Make -->
                  <div>
                    <label for="vehicle_make" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Vehicle Make <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="vehicle_make"
                      v-model="formData.vehicle_make"
                      type="text"
                      placeholder="e.g., Toyota, Honda, Ford"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white"
                      :style="{ borderColor: brandColors.primary, '--tw-ring-color': brandColors.primary, 'outline': 'none' }"
                    />
                  </div>

                  <!-- Vehicle Model -->
                  <div>
                    <label for="vehicle_model" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Vehicle Model <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="vehicle_model"
                      v-model="formData.vehicle_model"
                      type="text"
                      placeholder="e.g., Camry, Civic, F-150"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white"
                      :style="{ borderColor: brandColors.primary, '--tw-ring-color': brandColors.primary, 'outline': 'none' }"
                    />
                  </div>

                  <!-- Vehicle Year -->
                  <div>
                    <label for="vehicle_year" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Vehicle Year <span class="text-red-500">*</span>
                    </label>
                    <select
                      id="vehicle_year"
                      v-model.number="formData.vehicle_year"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white"
                      :style="{ borderColor: brandColors.primary, '--tw-ring-color': brandColors.primary, 'outline': 'none' }"
                    >
                      <option value="" disabled>Select year</option>
                      <option v-for="year in yearOptions" :key="year" :value="year">
                        {{ year }}
                      </option>
                    </select>
                  </div>

                  <!-- Plate Number -->
                  <div>
                    <label for="plate_number" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Plate Number <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="plate_number"
                      v-model="formData.plate_number"
                      type="text"
                      placeholder="e.g., ABC-1234"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white font-mono uppercase"
                      :style="{ borderColor: brandColors.primary, '--tw-ring-color': brandColors.primary, 'outline': 'none' }"
                    />
                  </div>
                </div>
              </div>

              <!-- Vehicle Photos Management -->
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Vehicle Photos <span class="text-red-500">*</span>
                </h4>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  All 4 side photos are required (Front, Back, Left, Right). Click on existing photos to replace them.
                </p>

                <!-- 4-Slot Photo Input Grid -->
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <!-- FRONT -->
                  <div class="text-center">
                    <label for="photo_front" class="cursor-pointer block">
                      <div class="border-2 border-dashed rounded-lg p-3 transition relative overflow-hidden"
                      :style="{ backgroundColor: brandColors.primary }">
                        <!-- Show existing photo if available and not deleted -->
                        <template v-if="existingPhotos.front && !deletedPhotoIds.includes(existingPhotos.front.vehicle_photo_id)">
                          <img
                            :src="getPhotoUrl(existingPhotos.front.photo_path)"
                            alt="Front View"
                            class="w-full h-16 object-cover rounded"
                          />
                        </template>
                        <!-- Show placeholder if no existing photo -->
                        <template v-else>
                          <img
                            src="@/assets/Front.png"
                            alt="Front View"
                            class="w-full h-16 object-contain rounded"
                          />
                        </template>
                      </div>
                      <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        FRONT <span class="text-red-500">*</span>
                      </p>
                    </label>
                    <input
                      id="photo_front"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      class="hidden"
                      @change="handlePhotoSelect('front', $event)"
                    />
                    <!-- Remove button for existing photo -->
                    <button
                      v-if="existingPhotos.front && !deletedPhotoIds.includes(existingPhotos.front.vehicle_photo_id)"
                      type="button"
                      @click="removeExistingPhoto('front')"
                      class="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Remove
                    </button>
                    <p v-if="errors.photo_front" class="mt-1 text-xs text-red-600 dark:text-red-400">
                      {{ errors.photo_front }}
                    </p>
                  </div>

                  <!-- BACK -->
                  <div class="text-center">
                    <label for="photo_back" class="cursor-pointer block">
                      <div class="border-2 border-dashed rounded-lg p-3 transition relative overflow-hidden"
                      :style="{ backgroundColor: brandColors.primary }">
                        <template v-if="existingPhotos.back && !deletedPhotoIds.includes(existingPhotos.back.vehicle_photo_id)">
                          <img
                            :src="getPhotoUrl(existingPhotos.back.photo_path)"
                            alt="Back View"
                            class="w-full h-16 object-cover rounded"
                          />
                        </template>
                        <template v-else>
                          <img
                            src="@/assets/Back.png"
                            alt="Back View"
                            class="w-full h-16 object-contain rounded"
                          />
                        </template>
                      </div>
                      <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        BACK <span class="text-red-500">*</span>
                      </p>
                    </label>
                    <input
                      id="photo_back"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      class="hidden"
                      @change="handlePhotoSelect('back', $event)"
                    />
                    <button
                      v-if="existingPhotos.back && !deletedPhotoIds.includes(existingPhotos.back.vehicle_photo_id)"
                      type="button"
                      @click="removeExistingPhoto('back')"
                      class="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Remove
                    </button>
                    <p v-if="errors.photo_back" class="mt-1 text-xs text-red-600 dark:text-red-400">
                      {{ errors.photo_back }}
                    </p>
                  </div>

                  <!-- LEFT -->
                  <div class="text-center">
                    <label for="photo_left" class="cursor-pointer block">
                      <div class="border-2 border-dashed rounded-lg p-3 transition relative overflow-hidden"
                      :style="{ backgroundColor: brandColors.primary }">
                        <template v-if="existingPhotos.left && !deletedPhotoIds.includes(existingPhotos.left.vehicle_photo_id)">
                          <img
                            :src="getPhotoUrl(existingPhotos.left.photo_path)"
                            alt="Left View"
                            class="w-full h-16 object-cover rounded"
                          />
                        </template>
                        <template v-else>
                          <img
                            src="@/assets/Left.png"
                            alt="Left View"
                            class="w-full h-16 object-contain rounded"
                          />
                        </template>
                      </div>
                      <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        LEFT <span class="text-red-500">*</span>
                      </p>
                    </label>
                    <input
                      id="photo_left"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      class="hidden"
                      @change="handlePhotoSelect('left', $event)"
                    />
                    <button
                      v-if="existingPhotos.left && !deletedPhotoIds.includes(existingPhotos.left.vehicle_photo_id)"
                      type="button"
                      @click="removeExistingPhoto('left')"
                      class="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Remove
                    </button>
                    <p v-if="errors.photo_left" class="mt-1 text-xs text-red-600 dark:text-red-400">
                      {{ errors.photo_left }}
                    </p>
                  </div>

                  <!-- RIGHT -->
                  <div class="text-center">
                    <label for="photo_right" class="cursor-pointer block">
                      <div class="border-2 border-dashed rounded-lg p-3 transition relative overflow-hidden"
                      :style="{ backgroundColor: brandColors.primary }">
                        <template v-if="existingPhotos.right && !deletedPhotoIds.includes(existingPhotos.right.vehicle_photo_id)">
                          <img
                            :src="getPhotoUrl(existingPhotos.right.photo_path)"
                            alt="Right View"
                            class="w-full h-16 object-cover rounded"
                          />
                        </template>
                        <template v-else>
                          <img
                            src="@/assets/Right.png"
                            alt="Right View"
                            class="w-full h-16 object-contain rounded"
                          />
                        </template>
                      </div>
                      <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        RIGHT <span class="text-red-500">*</span>
                      </p>
                    </label>
                    <input
                      id="photo_right"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      class="hidden"
                      @change="handlePhotoSelect('right', $event)"
                    />
                    <button
                      v-if="existingPhotos.right && !deletedPhotoIds.includes(existingPhotos.right.vehicle_photo_id)"
                      type="button"
                      @click="removeExistingPhoto('right')"
                      class="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Remove
                    </button>
                    <p v-if="errors.photo_right" class="mt-1 text-xs text-red-600 dark:text-red-400">
                      {{ errors.photo_right }}
                    </p>
                  </div>
                </div>

                <!-- New Photos Preview (2-column grid) -->
                <div v-if="hasNewPhotos" class="mt-6">
                  <h5 class="text-md font-semibold text-gray-900 dark:text-white mb-3">
                    New Photos to Upload
                  </h5>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <!-- Front Photo Preview -->
                    <div v-if="newPhotos.front" class="relative group">
                      <img
                        :src="getNewPhotoPreview('front')!"
                        alt="New Front Photo"
                        class="w-full h-32 object-cover rounded-lg border-2 border-blue-300 dark:border-blue-600"
                      />
                      <div class="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold uppercase">
                        FRONT
                      </div>
                      <button
                        type="button"
                        @click="removeNewPhoto('front')"
                        class="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-full shadow-lg transition-all duration-200"
                      >
                        <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <!-- Back Photo Preview -->
                    <div v-if="newPhotos.back" class="relative group">
                      <img
                        :src="getNewPhotoPreview('back')!"
                        alt="New Back Photo"
                        class="w-full h-32 object-cover rounded-lg border-2 border-blue-300 dark:border-blue-600"
                      />
                      <div class="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold uppercase">
                        BACK
                      </div>
                      <button
                        type="button"
                        @click="removeNewPhoto('back')"
                        class="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-full shadow-lg transition-all duration-200"
                      >
                        <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <!-- Left Photo Preview -->
                    <div v-if="newPhotos.left" class="relative group">
                      <img
                        :src="getNewPhotoPreview('left')!"
                        alt="New Left Photo"
                        class="w-full h-32 object-cover rounded-lg border-2 border-blue-300 dark:border-blue-600"
                      />
                      <div class="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold uppercase">
                        LEFT
                      </div>
                      <button
                        type="button"
                        @click="removeNewPhoto('left')"
                        class="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-full shadow-lg transition-all duration-200"
                      >
                        <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <!-- Right Photo Preview -->
                    <div v-if="newPhotos.right" class="relative group">
                      <img
                        :src="getNewPhotoPreview('right')!"
                        alt="New Right Photo"
                        class="w-full h-32 object-cover rounded-lg border-2 border-blue-300 dark:border-blue-600"
                      />
                      <div class="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold uppercase">
                        RIGHT
                      </div>
                      <button
                        type="button"
                        @click="removeNewPhoto('right')"
                        class="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-full shadow-lg transition-all duration-200"
                      >
                        <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  @click="handleClose"
                  :disabled="loading"
                  class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="loading"
                  class="px-4 py-2 text-white rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  :style="{ backgroundColor: brandColors.primary }"
                >
                  <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ loading ? 'Saving...' : 'Save Changes' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useBrand } from '@/composables/brand/useBrand';
import type { Vehicle, VehicleFormData, VehiclePhoto } from '@/types/vehicle';

const props = defineProps<{
  isOpen: boolean;
  vehicle: Vehicle | null;
  loading?: boolean;
}>();

const { brandColors } = useBrand();
const toast = useToast();

const emit = defineEmits<{
  close: [];
  save: [vehicleId: number, data: VehicleFormData, photoFiles: File[], photoSides: string[], deletedPhotoIds: number[]];
}>();

// Form data
const formData = ref<VehicleFormData>({
  vehicle_make: '',
  vehicle_model: '',
  vehicle_year: new Date().getFullYear(),
  plate_number: ''
});

// Photo management - side-based structure
const existingPhotos = ref<Record<string, VehiclePhoto | null>>({
  front: null,
  back: null,
  left: null,
  right: null
});

const newPhotos = ref<Record<string, File | null>>({
  front: null,
  back: null,
  left: null,
  right: null
});

const deletedPhotoIds = ref<number[]>([]);
const errors = ref<Record<string, string>>({});

const MAX_PHOTOS = 4;

// Year options (1900 to current year + 1)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + 1; year >= 1900; year--) {
    years.push(year);
  }
  return years;
});

// Check if any new photos are selected
const hasNewPhotos = computed(() => {
  return Object.values(newPhotos.value).some(photo => photo !== null);
});

// Watch for vehicle changes to populate form and photos
watch(() => props.vehicle, (newVehicle) => {
  if (newVehicle) {
    formData.value = {
      vehicle_make: newVehicle.vehicle_make,
      vehicle_model: newVehicle.vehicle_model,
      vehicle_year: newVehicle.vehicle_year,
      plate_number: newVehicle.plate_number
    };

    // Reset photo structures
    existingPhotos.value = { front: null, back: null, left: null, right: null };
    newPhotos.value = { front: null, back: null, left: null, right: null };
    deletedPhotoIds.value = [];

    // Map existing photos by side
    if (newVehicle.vehicle_photos) {
      newVehicle.vehicle_photos.forEach(photo => {
        if (photo.photo_side) {
          existingPhotos.value[photo.photo_side as keyof typeof existingPhotos.value] = photo;
        }
      });
    }

    errors.value = {};
  }
}, { immediate: true });

// Handle photo selection/replacement for specific side
function handlePhotoSelect(side: 'front' | 'back' | 'left' | 'right', event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    const file = target.files[0];

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      errors.value[`photo_${side}`] = 'Only JPEG, PNG, and JPG files are allowed';
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      errors.value[`photo_${side}`] = 'File size must not exceed 5MB';
      return;
    }

    newPhotos.value[side] = file;

    // Mark existing photo for deletion if replacing
    const existingPhoto = existingPhotos.value[side];
    if (existingPhoto && !deletedPhotoIds.value.includes(existingPhoto.vehicle_photo_id)) {
      deletedPhotoIds.value.push(existingPhoto.vehicle_photo_id);
    }

    errors.value[`photo_${side}`] = '';
  }
}

// Remove existing photo
function removeExistingPhoto(side: 'front' | 'back' | 'left' | 'right') {
  const photo = existingPhotos.value[side];
  if (photo && !deletedPhotoIds.value.includes(photo.vehicle_photo_id)) {
    deletedPhotoIds.value.push(photo.vehicle_photo_id);
    toast.info('Photo marked for deletion. Save to confirm.', { timeout: 3000 });
  }
}

// Remove new photo
function removeNewPhoto(side: 'front' | 'back' | 'left' | 'right') {
  newPhotos.value[side] = null;

  // If there was an existing photo that was marked for deletion, restore it
  const existingPhoto = existingPhotos.value[side];
  if (existingPhoto) {
    const index = deletedPhotoIds.value.indexOf(existingPhoto.vehicle_photo_id);
    if (index > -1) {
      deletedPhotoIds.value.splice(index, 1);
    }
  }
}

// Get photo preview URL for new photo
function getNewPhotoPreview(side: 'front' | 'back' | 'left' | 'right'): string | null {
  const file = newPhotos.value[side];
  return file ? URL.createObjectURL(file) : null;
}

function getPhotoUrl(path: string): string {
  const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
  // Below is for local
  // const baseUrl = process.env.VUE_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
  return `${baseUrl}/storage/${path}`;
}

function handleClose() {
  emit('close');
}

function handleSubmit() {
  if (props.vehicle) {
    errors.value = {};

    // Validate that all 4 side photos are covered (existing-not-deleted OR new upload)
    const sides = (['front', 'back', 'left', 'right'] as const);
    const sideLabels: Record<string, string> = {
      front: 'Front',
      back: 'Back',
      left: 'Left',
      right: 'Right',
    };
    let hasPhotoError = false;
    for (const side of sides) {
      const existingPhoto = existingPhotos.value[side];
      const existingOk =
        existingPhoto !== null &&
        !deletedPhotoIds.value.includes(existingPhoto!.vehicle_photo_id);
      const newOk = !!newPhotos.value[side];
      if (!existingOk && !newOk) {
        errors.value[`photo_${side}`] = `${sideLabels[side]} photo is required`;
        hasPhotoError = true;
      }
    }
    if (hasPhotoError) return;

    // Build File array and photoSides array from non-null sides
    const photoFiles: File[] = [];
    const photoSides: string[] = [];

    Object.entries(newPhotos.value).forEach(([side, file]) => {
      if (file) {
        photoFiles.push(file);
        photoSides.push(side);
      }
    });

    emit('save', props.vehicle.vehicle_id, { ...formData.value }, photoFiles, photoSides, deletedPhotoIds.value);
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.95);
}
</style>

